import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { useFormikContext } from "formik";
import { NormalizedDescriptionInfo } from "@common/types/normalizedDescription";
import { RuleGroupType } from "@common/types/transactionRule";
import { Button } from "@design-system/Button/Button";
import { PlusIcon, SplitIcon } from "@icons";
import styles from "../RuleForm.module.scss";
import { FormRuleConditionType, TransactionRuleFormSchema } from "../constants";
import { getDefaultFormRow } from "../modelToForm.helpers";
import { RuleConditionForm } from "./RuleConditionForm";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

export const RuleConditionsForm = ({
    groupType,
    normalizedDescriptions,
    shouldFocusEmptyCondition = false,
}: {
    groupType: RuleGroupType;
    normalizedDescriptions?: NormalizedDescriptionInfo[];
    shouldFocusEmptyCondition?: boolean;
}) => {
    const { values, setFieldValue } =
        useFormikContext<TransactionRuleFormSchema>();

    const addCondition = useCallback(() => {
        setFieldValue("conditions", [
            ...values.conditions,
            {
                id: Date.now().toString(),
                ...getDefaultFormRow(),
            },
        ]);
    }, [values.conditions, setFieldValue]);

    const removeCondition = useCallback(
        (id: string) => {
            setFieldValue(
                "conditions",
                values.conditions.filter((condition) => condition.id !== id),
            );
        },
        [values.conditions, setFieldValue],
    );

    const selectedConditionTypes = useMemo(
        () =>
            values.conditions
                .map((condition) => condition.conditionType)
                .filter(
                    (v) =>
                        v !== undefined &&
                        v !== FormRuleConditionType.BANK_DESCRIPTION &&
                        v !== FormRuleConditionType.AMOUNT,
                ),
        [values.conditions],
    );

    const canAddCondition = useMemo(
        () =>
            values.conditions.length <
            Object.values(FormRuleConditionType).length,
        [values.conditions.length],
    );

    const getAddConditionTooltip = () => {
        if (canAddCondition) {
            return undefined;
        }
        return "You can add only one condition per type";
    };

    const addConditionTooltip = getAddConditionTooltip();
    const firstEmptyConditionIndex = values.conditions.findIndex(
        (condition) => condition.conditionType === undefined,
    );

    return (
        <section className={styles.sectionConditions}>
            <header className={styles.header}>
                <div className={styles.group}>
                    <aside
                        className={classNames(
                            styles.sectionIcon,
                            styles.sectionIconConditions,
                        )}
                    >
                        <SplitIcon className="icon-color-gray-700" />
                    </aside>
                    <span className={styles.sectionTitle}>Conditions</span>
                </div>
            </header>

            <main className={styles.content}>
                <div className={styles.sectionRows}>
                    {values.conditions.map((condition, index) => (
                        <div className={styles.sectionRow} key={condition.id}>
                            <RuleConditionForm
                                shouldAutoFocus={
                                    shouldFocusEmptyCondition &&
                                    index === firstEmptyConditionIndex
                                }
                                condition={condition}
                                index={index}
                                onRemove={removeCondition}
                                selectedConditionTypes={selectedConditionTypes}
                                groupType={groupType}
                                normalizedDescriptions={normalizedDescriptions}
                            />
                        </div>
                    ))}

                    <div className={styles.sectionRow}>
                        <Tooltip content={addConditionTooltip}>
                            <Button
                                variant="primary"
                                size="md"
                                leftIcon={<PlusIcon />}
                                onClick={addCondition}
                                type="button"
                                disabled={!canAddCondition}
                            >
                                Add
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            </main>
        </section>
    );
};
